// Mapeias os operadores conforme utilizado no componente visual de filtros
// para símbolo utilizado na linguagem de filtros de API (baseada em MongoDB)

import { v4 as uuidv4 } from 'uuid'
import {
  FilterQuery,
  datepickerToISOFormat
} from '@yes.technology/react-toolkit'
import { FieldValues } from '../types'
import { Operator } from '../types/operators'
import { isSpecialRelationFieldByObjectclass } from 'shared/utils/specialRelationFields/specialRelationFields'
import { FieldValuesValue } from '../types/fields'
import XRegExp from 'xregexp'
import { FilterFieldType } from './fieldType'

// Ver https://wiki.lb1.tecnologiafox.com.br/index.php/RFC_Y5_-_Linguagem_de_filtros
const operatorsMapping = {
  gt: '$gt',
  gte: '$gte',
  lt: '$lt',
  lte: '$lte',
  in: '$in',
  nin: '$nin',
  eq: '$eq',
  ne: '$ne',
  REGEX: '$regex'
}

type ValidationResult = {
  success: boolean
  message?: string
}

// Retorna uma matriz de strings de correspondência entre os delimitadores
// mais externos à esquerda e à direita ou uma matriz de objetos com partes
// de correspondência detalhadas e dados de posição.
function splitComposition(composition: string): any[] {
  return XRegExp.matchRecursive(composition, '\\(', '\\)', 'gi', {
    valueNames: ['unmatch', null, 'match', null]
  })
}

function validParentheses(composition: string): boolean {
  let depth = 0
  if (!composition) return true

  for (let i = 0; i < composition.length; i++) {
    if (composition[i] === '(') {
      depth++
    } else if (composition[i] === ')') {
      depth--
    }
    if (depth < 0) return false
  }
  if (depth > 0) return false
  return true
}

// TODO: Comentários e refatoração
function validateLogicalStructure(composition: string): ValidationResult {
  const parts = splitComposition(composition)
  const len = parts.length

  if (len === 0) {
    // validar 1 OR 2 OR 3
    // validar: 1
    if (
      !/^(\s*(([1-9]\d*)+[\s+]+[a-z]+[\s+]+([1-9]\d*)|[1-9]\d*)(\s+|$))+$/i.test(
        composition.trim()
      )
    ) {
      return {
        success: false,
        message: `Composição inválida em: ${composition}`
      }
    }
  } else if (len === 1) {
    return validateLogicalStructure(parts[0].value.trim())
  } else if (len === 2) {
    if (parts[1].name === 'unmatch') {
      if (!/^(\s*([a-z]+[\s+]+[1-9]\d*))$/i.test(parts[1].value.trim())) {
        return {
          success: false,
          message: `Composição inválida em: ${parts[1].value.trim()}`
        }
      }
      return validateLogicalStructure(parts[0].value.trim())
    }

    if (parts[0].name === 'unmatch' && parts[0].value !== '') {
      if (!/^(\s*(([1-9]\d*)+[\s+]+[a-z]+))$/i.test(parts[0].value.trim())) {
        return {
          success: false,
          message: `Composição inválida em: ${parts[0].value.trim()}`
        }
      }
      return validateLogicalStructure(parts[1].value.trim())
    }
  } else if (len > 2) {
    // não permitir mais de 2 operadores por nível
    return {
      success: false,
      message: 'Permitido apenas 2 operadores por nível'
    }
  }

  return {
    success: true
  }
}

// exemplo de validação: (3 OR (((1) OR 4) AND 2))
export function validateComposition(
  composition: string,
  totalGroup: number
): ValidationResult {
  if (composition === '') {
    return { success: false, message: 'Composição Filtro é obrigatório' }
  }

  if (!validParentheses(composition)) {
    return { success: false, message: 'Parenteses inválidos' }
  }

  const compositionWithoutParentheses = composition.replace(/[()]/g, '')

  if (!/^(\s*(OR|AND|\d+|\s+)(\s+|$))+$/i.test(compositionWithoutParentheses)) {
    return { success: false, message: 'Operador não permitido' }
  }

  const onlyNumbers = compositionWithoutParentheses
    .replace(/\s/g, ';')
    .replace(/[^0-9]/g, ';')
  const listGroups = onlyNumbers.split(';').filter((el) => el !== '')
  const listUniqueGroups = new Set(listGroups)

  if (listUniqueGroups.size !== listGroups.length) {
    return { success: false, message: 'Não é permitido repetir grupo' }
  }

  const doNotExist: number[] = []
  for (const group of Array.from(listUniqueGroups.values())) {
    if (Number(group) > totalGroup) {
      doNotExist.push(Number(group))
    }
  }
  if (doNotExist.length) {
    return {
      success: false,
      message: `Grupo${doNotExist.length > 1 ? 's' : ''} ${doNotExist.join(
        ', '
      )} não existe${doNotExist.length > 1 ? 'm' : ''}`
    }
  }

  return validateLogicalStructure(composition)
}

export function makeDesFilter(operator: Operator, str: string) {
  return str
}

// O parâmetro items é um array contendo objetos com
// uuid e des. Precisamos apenas retornar o array contendo os ids
function makeIdsFilter(items: { uuid: string; des: string }[]) {
  return items.map((i) => i.uuid)
}

export function toYesFilter(values: FieldValues) {
  const group: FilterQuery[''][0] = {}

  let isValid = true

  if (!Object.keys(values).length) return false

  Object.keys(values).forEach((key) => {
    // Filtro cru proveniente do componente visual
    const raw = JSON.parse(JSON.stringify(values[key]))

    // A propriedade "values" pode ser uma string no caso de busca
    // por descrição, ou um array no caso de busca por ids
    let filterContent
    let property
    if (!areOperatorAndValuesCompatibles(raw.operator, raw.value)) {
      isValid = false
      return false
    }

    if (typeof raw.value === 'string') {
      const formattedValue =
        raw.type === FilterFieldType.Datetime
          ? datepickerToISOFormat(raw.value)
          : raw.value
      property = 'des'
      filterContent = formattedValue || ''
    } else if (
      typeof raw.value?.[0]?.objectclassUuid === 'string' &&
      isSpecialRelationFieldByObjectclass(raw.value?.[0]?.objectclassUuid)
    ) {
      raw.operator = raw.operator === 'in' ? 'eq' : 'ne'
      raw.type = FilterFieldType.String
      raw.value = raw.value[0].code
      property = 'des'
      filterContent = makeDesFilter(raw.operator, raw.value)
    } else {
      property = 'uuid'
      filterContent = makeIdsFilter(raw.value)
    }
    const filterType: Record<string, string | string[]> = {}
    const operator =
      operatorsMapping[raw.operator as keyof typeof operatorsMapping]
    filterType[operator] = filterContent
    group[key + '.' + property] = filterType
  })

  if (!isValid) {
    return false
  }

  return {
    $and: [group]
  }
}

// Validar se o operador é compatível com os valores selecionados
//
// Não podemos utilizar o operador 'in' com uma 'string', por exemplo
function areOperatorAndValuesCompatibles(
  operator: Operator,
  value: FieldValuesValue
) {
  let validOperators = {}
  if (typeof value === 'string') {
    validOperators = {
      gt: true,
      gte: true,
      lt: true,
      lte: true,
      eq: true,
      ne: true,
      REGEX: true
    }
  } else if (Array.isArray(value)) {
    validOperators = {
      in: true,
      nin: true
    }
  }

  if (operator in validOperators) {
    return true
  }
  return false
}

// formatArrayValue recebe um array de IDs
// provenientes de uma query no formato YesQueryLanguage
// e converte para um array de objetos esperados
// pelo componente local
function formatArrayValue(values: any) {
  const newValues: any = []
  values.forEach((val: any) => {
    newValues.push({
      id: val,
      uuid: val,
      des: val /* Pela query não temos como receber a DES real do objeto */
    })
  })
  return newValues
}

const valueFormatters = {
  $in: formatArrayValue,
  DEFAULT: (value: any) => value
}

function formatValue(rawOperatorId: string, value: any) {
  const formatter =
    valueFormatters[rawOperatorId as keyof typeof valueFormatters] ||
    valueFormatters.DEFAULT
  return formatter(value)
}

function getFieldKey(rawKey: string) {
  return rawKey.replace('.des', '').replace('.uuid', '')
}

function compositionBooleanOperator(operatorKey: string) {
  if (operatorKey === '$and') {
    return 'AND'
  } else if (operatorKey === '$or') {
    return 'OR'
  }
  console.log('Operador inválido')
  return '?'
}

const rawToOperator = {
  $gt: 'gt',
  $gte: 'gte',
  $lt: 'lt',
  $lte: 'lte',
  $in: 'in',
  $nin: 'nin',
  $eq: 'eq',
  $ne: 'ne',
  $regex: 'REGEX',
  $filteraction: 'filteraction'
}

// TODO: improve types
export function decodeQuery(
  query: Record<string, any> | string,
  isQueryRoot = true
) {
  let groupValues: Record<string, any> = {}
  let composition = ''

  let queryObject: Record<string, any> = {}
  try {
    queryObject = typeof query === 'string' ? JSON.parse(query) : query
  } catch (e) {
    return {
      values: groupValues,
      composition
    }
  }

  let isGroup = false
  let isBoolean = false
  Object.keys(queryObject).forEach((key) => {
    if (key === '$and' || key === '$or') {
      isBoolean = true
    } else {
      isGroup = true
    }
  })

  // Se for grupo, podemos devolver a
  // estrutura dos values
  if (isGroup) {
    const groupContent: Record<string, any> = {}
    Object.entries(queryObject).forEach(([rawFieldKey, rawFieldValue]) => {
      let rawOperatorId: keyof typeof rawToOperator = '$eq'
      let value = rawFieldValue

      if (rawFieldValue && typeof rawFieldValue === 'object') {
        // TODO: Em caso de mais de 1 entry, retornar erro não suportado
        ;[rawOperatorId, value] = Object.entries(rawFieldValue)[0] as [
          keyof typeof rawToOperator,
          any
        ]
      }

      value = formatValue(rawOperatorId, value)

      const fieldKey = getFieldKey(rawFieldKey)
      groupContent[fieldKey] = {
        operator: rawToOperator[rawOperatorId],
        value
      }
    })
    const myUuid = uuidv4()
    groupValues[myUuid] = groupContent
    composition = myUuid
  }

  // Se for lógica booleana, parseamos seu filho
  // recursivamente
  if (isBoolean) {
    Object.keys(queryObject).forEach((key) => {
      const compositionIds: string[] = []
      queryObject[key].forEach((block: any) => {
        const decoded = decodeQuery(block, false)
        groupValues = Object.assign(groupValues, decoded.values)
        compositionIds.push(decoded.composition)
      })
      let newComposition = compositionIds
        .reverse()
        .join(' ' + compositionBooleanOperator(key) + ' ')
      if (compositionIds.length > 1) {
        newComposition = '(' + newComposition + ')'
      }
      composition = composition + newComposition
    })
  }

  const rootGroupValues: Record<string, any> = {}

  if (isQueryRoot) {
    const groupKeys = Object.keys(groupValues)

    groupKeys.forEach((key, index) => {
      const groupIndex = (groupKeys.length - index).toString()
      composition = composition.replace(key, groupIndex)
      rootGroupValues[groupIndex] = groupValues[key]
    })
  }

  return {
    values: isQueryRoot ? rootGroupValues : groupValues,
    composition
  }
}
